import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { Layout, Button, Tooltip, Select } from "antd";
import {
	MenuFoldOutlined,
	MenuUnfoldOutlined,
	SearchOutlined,
	ExpandOutlined,
	CompressOutlined,
	ShopOutlined,
	BellOutlined,
} from "@ant-design/icons";
import Logo from "./Logo";
import NavProfile from "./NavProfile";
import NavSearch from "./NavSearch";
import SearchInput from "./NavSearch/SearchInput";
import IntlMessage from "components/util-components/IntlMessage";
import navigationConfig from "configs/NavigationConfig";
import { APP_PREFIX_PATH } from "configs/AppConfig";
import { toggleCollapsedNav, onMobileNavToggle } from "redux/actions/Theme";
import { setSelectedTenant } from "redux/actions/User";
import { SELECTED_TENANT_STORAGE_KEY } from "configs/AppConfig";
import {
	NAV_TYPE_TOP,
	NAV_TYPE_SIDE,
	TEMPLATE,
} from "constants/ThemeConstant";
import utils from "utils";
import "./header-nav-dimo.css";

const { Header } = Layout;

const HeaderNavFullscreen = ({ isDark }) => {
	const [fs, setFs] = useState(
		() => typeof document !== "undefined" && !!document.fullscreenElement,
	);

	useEffect(() => {
		const onFs = () => setFs(!!document.fullscreenElement);
		document.addEventListener("fullscreenchange", onFs);
		return () => document.removeEventListener("fullscreenchange", onFs);
	}, []);

	const toggle = () => {
		if (!document.fullscreenElement) {
			document.documentElement.requestFullscreen?.().catch(() => {});
		} else {
			document.exitFullscreen?.().catch(() => {});
		}
	};

	return (
		<Tooltip title={fs ? "Sair da tela cheia" : "Tela cheia"}>
			<Button
				type="text"
				className={`header-nav-tool-btn ${isDark ? "header-nav-tool-btn--dark" : "header-nav-tool-btn--light"}`}
				icon={fs ? <CompressOutlined /> : <ExpandOutlined />}
				onClick={toggle}
				aria-label="Alternar tela cheia"
			/>
		</Tooltip>
	);
};

export const HeaderNav = (props) => {
	const {
		navCollapsed,
		mobileNav,
		navType,
		headerNavColor,
		toggleCollapsedNav,
		onMobileNavToggle,
		isMobile,
		currentTheme,
		user,
		setTenant,
	} = props;
	const [searchActive, setSearchActive] = useState(false);
	const location = useLocation();

	const currentRouteInfo = useMemo(
		() => utils.getRouteInfo(navigationConfig, location.pathname),
		[location.pathname],
	);

	const selectedTenantName = useMemo(() => {
		const tenants = user?.tenants || [];
		const selected = user?.selectedTenant;
		const match = tenants.find((t) => String(t.id) === String(selected));
		return match?.nome || tenants[0]?.nome || null;
	}, [user?.tenants, user?.selectedTenant]);

	const onSearchClose = () => setSearchActive(false);
	const onSearchActive = () => setSearchActive(true);

	const onToggle = () => {
		if (!isMobile) {
			toggleCollapsedNav(!navCollapsed);
		} else {
			onMobileNavToggle(!mobileNav);
		}
	};

	const isOperationDashboard = location.pathname.startsWith(
		`${APP_PREFIX_PATH}/dashboard-teste-bateria`,
	);
	const isNavTop = navType === NAV_TYPE_TOP;
	const isDesktopSideNav = navType === NAV_TYPE_SIDE && !isMobile;
	const isDarkTheme = currentTheme === "dark";
	const showCollapseToggle = !(isNavTop && !isMobile);

	const navMode = useMemo(() => {
		if (!headerNavColor) {
			return utils.getColorContrast(isDarkTheme ? "#000000" : "#ffffff");
		}
		return utils.getColorContrast(headerNavColor);
	}, [isDarkTheme, headerNavColor]);

	const navBgColor = isDarkTheme
		? TEMPLATE.HEADER_BG_DEFAULT_COLOR_DARK
		: TEMPLATE.HEADER_BG_DEFAULT_COLOR_LIGHT;
	const isModernLightHeader =
		!isDarkTheme &&
		(!headerNavColor ||
			headerNavColor.toLowerCase() === "#ffffff" ||
			headerNavColor.toLowerCase() === "#fff");

	const tenants = user?.tenants || [];
	const selectedTenant = user?.selectedTenant;

	useEffect(() => {
		if (!isMobile) {
			onSearchClose();
		}
	}, [isMobile]);

	return (
		<Header
			className={`app-header app-header--dimo ${navMode} ${
				isDarkTheme ? "dark" : "light"
			} ${isModernLightHeader ? "app-header--modern-light" : ""} ${
				isDesktopSideNav ? "app-header--with-side-nav" : ""
			} ${isDesktopSideNav && navCollapsed ? "app-header--side-nav-collapsed" : ""}`}
			style={{
				backgroundColor: headerNavColor || navBgColor,
				display: "flex",
				alignItems: "center",
				padding: 0,
				height: TEMPLATE.HEADER_HEIGHT,
				lineHeight: `${TEMPLATE.HEADER_HEIGHT}px`,
				boxShadow: "none",
				borderBottom: isModernLightHeader
					? "1px solid #e5e7eb"
					: undefined,
			}}
		>
			<div
				className={`app-header-wrapper ${isNavTop ? "layout-top-nav" : ""}`}
				style={{
					display: "flex",
					width: "100%",
					alignItems: "stretch",
					minHeight: TEMPLATE.HEADER_HEIGHT,
				}}
			>
				{!isDesktopSideNav ? <Logo logoType={navMode} mobileLogo /> : null}
				<div
					className="nav"
					style={{
						flex: 1,
						minWidth: 0,
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
					}}
				>
					<div className="header-nav-row">
						<div className="header-nav-left">
							{showCollapseToggle ? (
								<button
									type="button"
									className={`header-nav-toggle-btn ${
										isModernLightHeader || !isDarkTheme
											? "header-nav-toggle-btn--light"
											: "header-nav-toggle-btn--dark"
									}`}
									onClick={onToggle}
									aria-label={
										navCollapsed || isMobile
											? "Expandir menu"
											: "Recolher menu"
									}
								>
									{navCollapsed || isMobile ? (
										<MenuUnfoldOutlined />
									) : (
										<MenuFoldOutlined />
									)}
								</button>
							) : null}
							{isMobile ? (
								<button
									type="button"
									className="ant-menu-item ant-menu-item-only-child"
									style={{
										background: "none",
										border: 0,
										cursor: "pointer",
									}}
									onClick={onSearchActive}
								>
									<SearchOutlined />
								</button>
							) : (
								<>
									{currentRouteInfo?.title && (
										<div className="header-nav-page-title">
											<IntlMessage
												id={currentRouteInfo.title}
											/>
										</div>
									)}
									<div
										className={`header-nav-search header-nav-search--wide ${
											isModernLightHeader || navMode === "dark"
												? "header-nav-search--light"
												: "header-nav-search--dark"
										}`}
									>
										<SearchInput
											close={onSearchClose}
											active={searchActive}
										/>
									</div>
								</>
							)}
						</div>
						<div className="header-nav-spacer" />
						<div className="header-nav-tools">
							{tenants.length > 1 ? (
								<div className="header-nav-tenant">
									<ShopOutlined className="header-nav-tenant__icon" />
									<Select
										size="small"
										bordered={false}
										value={
											selectedTenant ||
											String(tenants[0]?.id || "")
										}
										style={{ minWidth: 180 }}
										placeholder="Cliente"
										onChange={(value) => {
											localStorage.setItem(
												SELECTED_TENANT_STORAGE_KEY,
												value,
											);
											setTenant(value);
											window.location.reload();
										}}
									>
										{tenants.map((t) => (
											<Select.Option
												key={t.id}
												value={String(t.id)}
											>
												{t.nome}
											</Select.Option>
										))}
									</Select>
								</div>
							) : selectedTenantName ? (
								<div className="header-nav-tenant header-nav-tenant--readonly">
									<ShopOutlined className="header-nav-tenant__icon" />
									<span>{selectedTenantName}</span>
								</div>
							) : null}
							{isOperationDashboard ? (
								<div
									id="operation-topbar-mount"
									className="header-nav-operation-mount"
								/>
							) : null}
							<Tooltip title="Notificações">
								<Button
									type="text"
									className="header-nav-tool-btn header-nav-tool-btn--light header-nav-notification-btn"
									icon={<BellOutlined />}
									aria-label="Notificações"
								/>
							</Tooltip>
							<HeaderNavFullscreen isDark={!isModernLightHeader && isDarkTheme} />
						</div>
						<NavProfile isModernLight={isModernLightHeader} />
					</div>
					<NavSearch
						active={searchActive}
						close={onSearchClose}
						headerNavColor={headerNavColor}
					/>
				</div>
			</div>
		</Header>
	);
};

const mapStateToProps = ({ theme, user }) => {
	const {
		navCollapsed,
		navType,
		headerNavColor,
		mobileNav,
		currentTheme,
		direction,
	} = theme;
	return {
		navCollapsed,
		navType,
		headerNavColor,
		mobileNav,
		currentTheme,
		direction,
		user,
	};
};

export default connect(mapStateToProps, {
	toggleCollapsedNav,
	onMobileNavToggle,
	setTenant: setSelectedTenant,
})(HeaderNav);
